/**
 * Challenge create / update schemas. See AGENT.md §6.3 for the field list
 * and the data/metadata zone classification.
 *
 * A `DRAFT` is created with the identifying trio (segmentId, languoidCode,
 * domain). Everything else is filled in progressively by the wizard with
 * per-field autosave (AGENT.md §21.1), so the gold/description/context fields
 * are optional here and default to empty strings.
 *
 * NOTE on the firewall: the gold and transcript fields below live in the
 * METADATA zone. They are plain strings at the HTTP boundary; branding via
 * `asMetadata` happens in the service layer, and the firewall types in
 * `../firewall.ts` guarantee they can never reach the Gemini gateway.
 */

import { z } from 'zod';
import { ChallengeState, type ChallengeStateName } from '../states.js';
import type { UtteranceSummary } from './utterance.js';
import {
  languoidCodeSchema,
  segmentIdSchema,
  difficultyLevelSchema,
  recordingFlowSchema,
  paginationQuerySchema,
} from './primitives.js';

export const challengeCreateSchema = z.object({
  segmentId: segmentIdSchema,
  languoidCode: languoidCodeSchema,
  domain: z.string().min(1, 'Domain is required').max(120),

  // Metadata zone — optional at draft time.
  segmentContext: z.string().max(8000).default(''),
  recordingFlow: recordingFlowSchema.default('SOLO'),
  /** The challenge's single English comprehension question (per-challenge §0). */
  questionText: z.string().max(8000).default(''),
  goldTranslation: z.string().max(8000).default(''),
  goldAnswer: z.string().max(8000).default(''),
  translationLevel: difficultyLevelSchema.default('HARD'),
  reasoningLevel: difficultyLevelSchema.default('HARD'),
  translationChallengeDescription: z.string().max(8000).default(''),
  reasoningChallengeDescription: z.string().max(8000).default(''),
});
/** Server-side, fully-resolved shape (defaults applied). */
export type ChallengeCreateInput = z.infer<typeof challengeCreateSchema>;
/** Wire shape sent by the client: fields with defaults are optional. */
export type ChallengeCreateRequest = z.input<typeof challengeCreateSchema>;

/**
 * Update is the create shape with every field optional. `segmentId`,
 * `languoidCode`, and `state` are intentionally NOT updatable here — segment
 * identity is fixed once created, and state changes only via the dedicated
 * transition endpoints (AGENT.md §20.4).
 */
export const challengeUpdateSchema = challengeCreateSchema.partial().omit({
  segmentId: true,
  languoidCode: true,
});
export type ChallengeUpdateInput = z.infer<typeof challengeUpdateSchema>;

/** Optional comment body shared by transition endpoints that require one. */
export const transitionCommentSchema = z.object({
  comment: z.string().min(1, 'A comment is required').max(2000),
});
export type TransitionCommentInput = z.infer<typeof transitionCommentSchema>;

/** Zod enum over the challenge state names (built from the state machine table). */
export const challengeStateSchema = z.enum(
  Object.values(ChallengeState) as [ChallengeStateName, ...ChallengeStateName[]],
);

/**
 * Challenge authoring direction (mirrors the Prisma `ChallengeDirection` enum).
 * FORWARD = the team authors in their language; REVERSE = the platform authors
 * in English and the team translates in (docs/reverse-challenges-plan.md).
 */
export const challengeDirectionSchema = z.enum(['FORWARD', 'REVERSE']);
export type ChallengeDirection = z.infer<typeof challengeDirectionSchema>;

/**
 * List query for a team's challenges: cursor pagination + optional state and
 * direction filters. `states` is a comma-separated list (`?states=READY_TO_TEST,TESTED`)
 * so a queue surface (test runner, judgement) fetches exactly the states it
 * needs server-side, instead of client-side filtering only the first page —
 * which under-counts once a team has more challenges than one page holds.
 * `direction` scopes a surface to FORWARD or REVERSE challenges: the web
 * judging/pre-test surfaces pass FORWARD, because reverse verdicts come from the
 * team's back-translation via the sheet round-trip, not the PASS/FAIL UI.
 */
export const challengeListQuerySchema = paginationQuerySchema.extend({
  states: z.preprocess((v) => {
    const arr = typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v;
    return Array.isArray(arr) && arr.length === 0 ? undefined : arr;
  }, z.array(challengeStateSchema).optional()),
  direction: challengeDirectionSchema.optional(),
});
export type ChallengeListQuery = z.infer<typeof challengeListQuerySchema>;

/**
 * Queue/list view of a challenge. Deliberately excludes the metadata-zone gold
 * fields (goldTranslation, goldAnswer, descriptions) — those are gated behind
 * `can_view_gold` on the detail endpoint, not exposed in list responses.
 */
export interface ChallengeSummary {
  id: string;
  segmentId: string;
  languoidCode: string;
  domain: string;
  state: ChallengeStateName;
  /** FORWARD (team authors in-language) or REVERSE (platform authors in English, team translates). */
  direction: ChallengeDirection;
  recordingFlow: 'SOLO' | 'COORDINATED';
  designerId: string;
  currentAssigneeId: string | null;
  /** Computed at APPROVED (§7.3 validateRouting); null until then. */
  uploadRoute: 'CHALLENGE_SET' | 'NON_CHALLENGE_SET' | null;
  /**
   * Why a test can't run yet — `validateTestable`'s reason (missing question /
   * reference gold) — or null when the challenge IS runnable. Derived from field
   * PRESENCE only (never the gold text), so it stays firewall-safe in list
   * responses. Meaningful for READY_TO_TEST: the test runner uses it to separate
   * genuinely-runnable segments from recorded stubs still awaiting their
   * question/reference on the sheet.
   */
  testBlockedReason: string | null;
  createdAt: string;
  lastModifiedAt: string;
}

export interface ChallengeListResponse {
  items: ChallengeSummary[];
  nextCursor: string | null;
}

/** Counts for a team's READY_TO_TEST challenges (the team-selector badges). */
export interface ChallengeTestReadyCount {
  /** Genuinely runnable now (metadata + question audio present). */
  readyToTest: number;
  /** Recorded but not runnable yet (missing question text/gold or confirmed question audio). */
  notRunnable: number;
}

/**
 * Designer-facing detail of a challenge (AGENT.md §6.3, §21.1) — the shape the
 * design wizard loads to seed its form. Unlike `ChallengeSummary`, this DOES
 * carry the metadata-zone gold fields, difficulty levels, and design notes, so
 * the endpoint behind it is gated on `can_design`. The utterance set is bundled
 * in so the wizard hydrates in a single round trip.
 *
 * FIREWALL note: gold/transcripts are metadata. Returning them to an authorized
 * designer is fine — they authored them — but they remain `MetadataField`s in
 * the service layer and can never reach the Gemini gateway.
 */
export interface ChallengeDesign {
  id: string;
  segmentId: string;
  languoidCode: string;
  domain: string;
  state: ChallengeStateName;
  recordingFlow: 'SOLO' | 'COORDINATED';
  segmentContext: string;
  /** The challenge's single English question (derived from its utterances). */
  questionText: string | null;
  goldTranslation: string;
  goldAnswer: string;
  translationLevel: 'HARD' | 'EXTRA_HARD';
  reasoningLevel: 'HARD' | 'EXTRA_HARD';
  translationChallengeDescription: string;
  reasoningChallengeDescription: string;
  utterances: UtteranceSummary[];
}
